import React, { useEffect, useMemo, useRef, useState } from "react";
import { Global } from "../helpers/Global";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import { Box, Paper, Typography, TextField, Button, Stack } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

export const CrearIntervencionManualGerente = () => {
  // =========================
  // Hooks / estado base
  // =========================
  const toast = useToast();

  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [centros, setCentros] = useState([]);

  const [usuarioId, setUsuarioId] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [ordenId, setOrdenId] = useState("");
  const [centroId, setCentroId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fin, setFin] = useState("");
  const [comentario, setComentario] = useState("");

  const [loadingData, setLoadingData] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cargandoCentros, setCargandoCentros] = useState(false);

  // Para distinguir cambio manual de cliente vs. asignado por la OT
  const clienteCambiadoManualmente = useRef(false);

  // =========================
  // Carga inicial (subordinados + clientes + OTs activas)
  // =========================
  useEffect(() => {
    cargarCombos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarCombos = async () => {
    setLoadingData(true);
    try {
      const [resSub, resCli, resOts] = await Promise.all([
        fetch(`${Global.url}usuarios/subordinados`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}clientes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}ordenes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      if (!resSub.ok) toast.error("No se pudieron cargar los usuarios del equipo.");
      if (!resCli.ok) toast.error("No se pudieron cargar los clientes.");
      if (!resOts.ok) toast.error("No se pudieron cargar las OTs.");

      const [dataSub, dataCli, dataOts] = await Promise.all([
        resSub.ok ? resSub.json() : [],
        resCli.ok ? resCli.json() : [],
        resOts.ok ? resOts.json() : [],
      ]);

      // Filtra OTs activas (mismo criterio que usas en gerente)
      const otsActivas = (Array.isArray(dataOts) ? dataOts : []).filter(
        (o) => !["cancelada", "finalizada"].includes((o.estado || "").toLowerCase())
      );

      setUsuarios(Array.isArray(dataSub) ? dataSub : []);
      setClientes(Array.isArray(dataCli) ? dataCli : []);
      setOrdenes(otsActivas);
    } catch (err) {
      console.error("Error al cargar datos iniciales:", err);
      toast.error("Error de conexión al cargar datos iniciales.");
      setUsuarios([]);
      setClientes([]);
      setOrdenes([]);
    } finally {
      setLoadingData(false);
    }
  };

  // =========================
  // OT -> Cliente + Centros (derivados)
  // =========================
  useEffect(() => {
    const fetchCentros = async () => {
      if (!ordenId) {
        setCentros([]);
        setCentroId("");
        return;
      }

      const ot = (ordenes || []).find((o) => String(o.id) === String(ordenId));
      const cliId = ot?.cliente_id ? String(ot.cliente_id) : "";

      // Al elegir OT, fijamos el cliente (NO se considera “manual”)
      clienteCambiadoManualmente.current = false;
      setClienteId(cliId);

      setCargandoCentros(true);
      try {
        const res = await fetch(`${Global.url}ordenes/${ordenId}/centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        if (!res.ok) {
          toast.warning("No se pudieron cargar los centros de la OT.");
          setCentros([]);
          return;
        }

        const data = await res.json().catch(() => null);
        const centrosData = Array.isArray(data) ? data : data?.data || [];
        setCentros(Array.isArray(centrosData) ? centrosData : []);
      } catch (err) {
        console.error("Error al obtener centros:", err);
        toast.error("Error de conexión al cargar centros.");
        setCentros([]);
      } finally {
        setCargandoCentros(false);
      }
    };

    fetchCentros();
  }, [ordenId, ordenes, toast]);

  // =========================
  // Cliente cambiado manualmente -> limpiar OT/centros
  // =========================
  useEffect(() => {
    if (!clienteId) return;

    if (clienteCambiadoManualmente.current) {
      setOrdenId("");
      setCentros([]);
      setCentroId("");
    }

    clienteCambiadoManualmente.current = false;
  }, [clienteId]);

  // =========================
  // Opciones ordenadas (Autocomplete)
  // =========================
  const usuariosOrdenados = useMemo(() => {
    return (usuarios || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      );
  }, [usuarios]);

  const clientesOrdenados = useMemo(() => {
    return (clientes || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [clientes]);

  // OTs filtradas por cliente si éste está seleccionado
  const ordenesFiltradas = useMemo(() => {
    return clienteId ? (ordenes || []).filter((o) => String(o.cliente_id) === String(clienteId)) : ordenes || [];
  }, [ordenes, clienteId]);

  const ordenesOrdenadas = useMemo(() => {
    return (ordenesFiltradas || [])
      .slice()
      .sort((a, b) => (a.titulo || "").localeCompare(b.titulo || ""));
  }, [ordenesFiltradas]);

  const centrosOrdenados = useMemo(() => {
    return (centros || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
  }, [centros]);

  const usuarioSeleccionado = usuariosOrdenados.find((u) => String(u.id) === String(usuarioId)) || null;
  const clienteSeleccionado = clientesOrdenados.find((c) => String(c.id) === String(clienteId)) || null;
  const ordenSeleccionada = ordenesOrdenadas.find((o) => String(o.id) === String(ordenId)) || null;
  const centroSeleccionado = centrosOrdenados.find((c) => String(c.id) === String(centroId)) || null;

  // =========================
  // Submit
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!usuarioId || !clienteId || !ordenId || !centroId || !inicio || !fin) {
      toast.warning("Usuario, cliente, OT, centro, inicio y fin son obligatorios.");
      return;
    }

    const dIni = new Date(inicio);
    const dFin = new Date(fin);
    if (Number.isNaN(dIni.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dIni) {
      toast.warning("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        usuario_id: parseInt(usuarioId, 10),
        cliente_id: parseInt(clienteId, 10),
        orden_id: parseInt(ordenId, 10),
        centro_id: parseInt(centroId, 10),
        inicio,
        fin,
        comentarios: comentario,
      };

      const res = await fetch(`${Global.url}ticajes/manual`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Intervención creada correctamente.");

        // reset
        setUsuarioId("");
        setClienteId("");
        setOrdenId("");
        setCentroId("");
        setCentros([]);
        setInicio("");
        setFin("");
        setComentario("");
        return;
      }

      const data = await res.json().catch(() => ({}));
      toast.error(data?.message || "No se pudo crear la intervención.");
    } catch (error) {
      console.error("Error al crear intervención:", error);
      toast.error("Error de conexión con el servidor.");
    } finally {
      setSaving(false);
    }
  };

  const limpiarFormulario = () => {
    setUsuarioId("");
    setClienteId("");
    setOrdenId("");
    setCentroId("");
    setCentros([]);
    setInicio("");
    setFin("");
    setComentario("");
    toast.info("Formulario limpiado.");
  };

  // =========================
  // Render
  // =========================
  return (
    <Box className="appContainer">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Inserción Manual de Intervención (Gerente)
        </Typography>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <Paper
            sx={{
              p: 2,
              width: "100%",
              maxWidth: 900,
            }}
          >
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                alignItems: "start",
              }}
            >
              {/* Usuario (subordinados) */}
              <Autocomplete
                size="small"
                options={usuariosOrdenados}
                getOptionLabel={(u) => `${u.nombre} ${u.apellidos || ""}`.trim()}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={usuarioSeleccionado}
                onChange={(_, newValue) => setUsuarioId(newValue ? String(newValue.id) : "")}
                loading={loadingData}
                renderInput={(params) => (
                  <TextField {...params} label="Usuario" placeholder="Escribe para buscar…" required />
                )}
                clearOnEscape
              />

              {/* Cliente (seleccionable → filtra OTs) */}
              <Autocomplete
                size="small"
                options={clientesOrdenados}
                getOptionLabel={(c) => c.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={clienteSeleccionado}
                onChange={(_, newValue) => {
                  clienteCambiadoManualmente.current = true;
                  setClienteId(newValue ? String(newValue.id) : "");
                }}
                loading={loadingData}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" placeholder="Escribe para buscar…" required />
                )}
                clearOnEscape
              />

              {/* OT (si hay cliente, ya filtradas) */}
              <Autocomplete
                size="small"
                options={ordenesOrdenadas}
                getOptionLabel={(o) => o.titulo || `OT #${o.id}`}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={ordenSeleccionada}
                onChange={(_, newValue) => {
                  setOrdenId(newValue ? String(newValue.id) : "");
                  setCentroId(""); // reset centro al cambiar OT
                }}
                loading={loadingData}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="OT"
                    placeholder={clienteId ? "Selecciona una OT del cliente" : "Selecciona una OT"}
                    required
                    helperText={clienteId && ordenesOrdenadas.length === 0 ? "Sin OTs para este cliente" : " "}
                  />
                )}
                clearOnEscape
              />

              {/* Centro (derivado de la OT) */}
              <Autocomplete
                size="small"
                options={centrosOrdenados}
                getOptionLabel={(c) => c.nombre || ""}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                value={centroSeleccionado}
                onChange={(_, newValue) => setCentroId(newValue ? String(newValue.id) : "")}
                loading={cargandoCentros}
                disabled={!ordenId || cargandoCentros || centrosOrdenados.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Centro"
                    placeholder={cargandoCentros ? "Cargando centros..." : "Selecciona un centro"}
                    required
                    helperText={
                      !ordenId
                        ? "Selecciona antes una OT"
                        : centrosOrdenados.length === 0
                        ? "Sin centros para esta OT"
                        : " "
                    }
                  />
                )}
                clearOnEscape
              />

              {/* Inicio */}
              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
                required
              />

              {/* Fin */}
              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={fin}
                onChange={(e) => setFin(e.target.value)}
                required
              />

              {/* Comentario */}
              <TextField
                label="Motivo o comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Motivo o notas sobre la intervención"
                multiline
                minRows={3}
                sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}
              />

              {/* Acciones */}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  gridColumn: "1 / -1",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  pt: 1,
                }}
              >
                <Button variant="outlined" onClick={limpiarFormulario} disabled={saving}>
                  Limpiar
                </Button>

                <Button type="submit" disabled={saving}>
                  {saving ? "Creando..." : "Crear intervención"}
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};
